"use client";

import axios from "axios";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useSession } from "@/app/hooks/useSession";
import { PremiumModal } from "@/components/premium/PremiumModal";
import SettingsMotionContent from "@/components/settings/SettingsMotionContent";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { LoadingState } from "@/components/shared/LoadingState";
import { PageHeader } from "@/components/shared/PageHeader";
import { trackSubscription } from "@/lib/analytics";

export default function SettingsPage() {
  const { update } = useSession();
  const tSettings = useTranslations("settings");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  useEffect(() => {
    if (!isReady) {
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.documentElement.style.overflow = "";
      };
    }
  }, [isReady]);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const response = await axios.post("/api/subscription/cancel", {
        immediately: false,
      });
      // Refresh session to update premium status
      update();

      // Track subscription cancellation
      trackSubscription("cancelled");

      toast.success(tSettings("subscriptionScheduledCancellation"), {
        description: response.data.messageKey
          ? tSettings(response.data.messageKey as any)
          : tSettings("retainAccessUntilPeriodEnd"),
      });
    } catch (err: any) {
      toast.error(tSettings("failedToCancelSubscription"), {
        description: err.response?.data?.error || tSettings("pleaseTryAgain"),
      });
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
      // have subscription section refresh its data
      setSubscriptionRefreshKey((prev) => prev + 1);
    }
  };

  return (
    <>
      <PageHeader />

      <div className="relative flex-grow overflow-auto">
        {/* Full-screen loader overlay while page motion animations and chunk rendering finish */}
        {!isReady && (
          <div className="fixed inset-0 z-50">
            <LoadingState />
          </div>
        )}
        <div className="bg-background flex flex-col">
          <main className="flex-1 overflow-y-auto min-h-0 w-full max-w-lg mx-auto px-3 py-8">
            <SettingsMotionContent
              onUpgradeClick={() => setShowPremiumModal(true)}
              onCancelClick={() => setShowCancelConfirm(true)}
              onReady={() => setIsReady(true)}
              subscriptionRefreshKey={subscriptionRefreshKey}
            />
          </main>

          {/* Cancel Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showCancelConfirm}
            onClose={() => !cancelLoading && setShowCancelConfirm(false)}
            onConfirm={handleCancelSubscription}
            title={tSettings("cancelSubscriptionTitle")}
            description={tSettings("cancelSubscriptionDescription")}
            confirmText={tSettings("yesCancelSubscription")}
            cancelText={tSettings("keepSubscription")}
            variant="destructive"
            loading={cancelLoading}
            autoCloseOnConfirm={false}
          />

          {/* Premium Modal */}
          <PremiumModal
            isOpen={showPremiumModal}
            onClose={() => setShowPremiumModal(false)}
            limitType={null}
          />
        </div>
      </div>
    </>
  );
}
